import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, ButtonGroup, Button } from 'reactstrap';
import Breadcrumb from 'Components/UI/Breadcrumb';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import TotalStats from './TotalStats';
import YearlyBookings from './MonthlyBookings';
import MonthlyBookingRevenue from './MonthlyBookingRevenue';
import Inquiries from './Inquiries';
import ProductCategories from './ProductCategories';
import { getDashboardData } from 'helpers/API Calls/Operations/dashboard';
import Spinner from 'Components/UI/Spinner';
import TopProducts from './TopProducts';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

type DateRange = [Date, Date] | null;
const getInitialDateRange = (mode: 'monthly' | 'weekly' | 'yearly'): [Date, Date] => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // end of current month
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1); // start of 12 months ago

  switch (mode) {
    case 'monthly':
      return [start, end];
    case 'weekly': {
      const weeklyStart = new Date();
      weeklyStart.setDate(now.getDate() - 7 * 4);
      return [weeklyStart, now];
    }
    case 'yearly': {
      const yearlyStart = new Date();
      yearlyStart.setFullYear(now.getFullYear() - 4);
      return [yearlyStart, now];
    }
  }
};

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [data, setData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'monthly' | 'weekly' | 'yearly'>('monthly');

  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange('monthly'));
  const handleDateRangeChange = (value: [Date | null, Date | null] | null) => {
    if (value && value[0] && value[1]) {
      const [start, end] = value;
      const diffInMs = end.getTime() - start.getTime();
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

      if (viewMode === 'monthly') {
        const diffInMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        if (diffInMonths < 0 || diffInMonths > 11) {
          alert('Please select a range between 1 to 12 months.');
          return;
        }
      }

      if (viewMode === 'weekly') {
        const weeks = diffInDays / 7;
        if (weeks < 1 || weeks > 20) {
          alert('Please select between 1 to 20 weeks.');
          return;
        }
      }

      if (viewMode === 'yearly') {
        const years = diffInDays / 365;
        if (years < 1 || years > 5) {
          alert('Please select between 1 to 5 years.');
          return;
        }
      }

      setDateRange([start, end]);
    } else {
      setDateRange(null);
    }
  };

  useEffect(() => {
    setDateRange(getInitialDateRange(viewMode));
  }, [viewMode]);

  useEffect(() => {
    if (isInitialLoad) {
      setIsLoading(true);  // full-page spinner
    } else {
      setChartLoading(true); // only chart section
    }
    const fetchData = async () => {
      const formattedDates = dateRange ? dateRange.map(date => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      }) : [];
      try{
        const queryString = formattedDates.length > 0
        ? `from_date=${formattedDates[0]}&to_date=${formattedDates[1]}&duration=${viewMode}`
        : '';
      const response = await getDashboardData(queryString);
      if (response.success) {
        setData(response.data);
      }
      }catch (error) {
        console.error("Error fetching dashboard data:", error);
      }finally {
      if (isInitialLoad) {
        setIsLoading(false);
        setIsInitialLoad(false);
      } else {
        setChartLoading(false);
      }
    }
    }
    fetchData();
  }, [dateRange]);

  // Create gradient for line chart
  const createGradient = (ctx: CanvasRenderingContext2D) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(85, 110, 230, 0.3)');
    gradient.addColorStop(1, 'rgba(85, 110, 230, 0.0)');
    return gradient;
  };

const YEARLY_BOOKINGS_DATA = data ? {
  labels: data?.bookings?.labels || [],
  datasets: data?.bookings?.datasets?.map(dataset => ({
    ...dataset,
    backgroundColor: function(context) {
      const chart = context.chart;
      const { ctx } = chart;
      return createGradient(ctx);
    },
    pointBackgroundColor: '#556ee6',
    pointBorderColor: '#fff',
    pointBorderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
  })) || []
} : { labels: [], datasets: [] };


  const STATS = data ? [
    {
      title: "Bookings This Year",
      count: data.bookingSummary.thisYear.count,
      revenue: data.bookingSummary.thisYear.totalAmount,
      icon: "bx bx-calendar",
      color: "primary"
    },
    {
      title: "Bookings This Month",
      count: data.bookingSummary.thisMonth.count,
      revenue: data.bookingSummary.thisMonth.totalAmount,
      icon: "bx bx-calendar-check",
      color: "success"
    },
    {
      title: "Bookings This Week",
      count: data.bookingSummary.thisWeek.count,
      revenue: data.bookingSummary.thisWeek.totalAmount,
      icon: "bx bx-calendar-event",
      color: "info"
    },
    {
      title: "Bookings Today",
      count: data.bookingSummary.today.count,
      revenue: data.bookingSummary.today.totalAmount,
      icon: "bx bxs-calendar-event",
      color: "warning"
    }
  ] : [];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#495057',
        bodyColor: '#495057',
        titleFont: {
          size: 13,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
        },
        padding: 12,
        borderColor: '#eff2f7',
        borderWidth: 1,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Revenue') {
              return ` $${context.parsed.y.toLocaleString()}`;
            }
            return ` ${context.parsed.y} bookings`;
          }
        }
      },
    },
    scales: {
      y: {
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false,
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          padding: 10
        }
      },
    },
    barThickness: 25,
    maxBarThickness: 40,
    layout: {
      padding: {
        left: 10,
        right: 10
      }
    }
  };

  const totalQuantity = data?.bookingsTable.reduce((sum: number, p: any) => sum + (p.bookings || 0), 0);
  const totalRevenue = data?.bookingsTable.reduce((sum: number, p: any) => sum + (p.totalAmount || 0), 0);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb items={[{ label: "Dashboard", link: "/dashboard" }]}/>
          {isLoading ? (
            <div className="position-relative vh-100"><Spinner/></div>
          ) :
            <>
              {/* Statistics Cards */}
              <TotalStats stats={STATS} />

              {/* Charts Row */}
           <Row>
              <Col xl={8}>
                <Card>
                  <CardBody className='text-end'>
                    <DateRangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      cleanable={false}
                      ranges={[]}
                      placeholder="Select Date Range"
                      appearance="default"
                      // disabledDate={(date) => {
                      //   if (viewMode === 'monthly') {
                      //     const [start] = dateRange || [];
                      //     if (start) {
                      //       const diffInMonths = (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
                      //       return diffInMonths > 11;
                      //     }
                      //   }

                      //   if (viewMode === 'weekly') {
                      //     const [start] = dateRange || [];
                      //     if (start) {
                      //       const daysDiff = (date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
                      //       return daysDiff > 7 * 20;
                      //     }
                      //   }

                      //   return false;
                      // }}
                    />
                    <ButtonGroup className='ms-2'>
                      <Button 
                        onClick={() => setViewMode('monthly')} 
                        className={`${viewMode === 'monthly' ? 'active' : ''}`}
                        color="primary"
                      >
                        Monthly
                      </Button>
                      <Button 
                        onClick={() => setViewMode('weekly')} 
                        className={`${viewMode === 'weekly' ? 'active' : ''}`}
                        color="primary"
                      >
                        Weekly
                      </Button>
                      <Button 
                        onClick={() => setViewMode('yearly')} 
                        className={`${viewMode === 'yearly' ? 'active' : ''}`}
                        color="primary"
                      >
                        Yearly
                      </Button>
                    </ButtonGroup>

                  </CardBody>
                  {chartLoading ? (
                    <div className="p-5 text-center"><Spinner /></div>
                  ) : (
                  <>
                    <CardBody className='pb-0'>
                      <Row>
                        <Col md={6}>
                          <Card className="border-0 shadow-sm mb-0 pb-0">
                            <CardBody className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <i className="bx bx-bar-chart-alt-2 text-primary fs-2 me-2"></i>
                                <div>
                                  <h4 className="mb-0 text-primary">{totalQuantity?.toLocaleString() || 0}</h4>
                                  <p className="text-muted mb-0">Total Bookings</p>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="border-0 shadow-sm mb-0 pb-0">
                            <CardBody className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <i className="bx bx-dollar-circle text-success fs-2 me-2"></i>
                                <div>
                                  <h4 className="mb-0 text-success">${totalRevenue?.toLocaleString() || 0}</h4>
                                  <p className="text-muted mb-0">Total Revenue</p>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </CardBody>
                    <YearlyBookings chartOptions={chartOptions} chartData={YEARLY_BOOKINGS_DATA} />

                    {/* Monthly Bookings Bar Chart */}
                    <MonthlyBookingRevenue 
                      barChartData={data?.bookingsTable || []} 
                      chartOptions={chartOptions} 
                    />

                    <TopProducts
                      dataColors='["--bs-primary", "--bs-warning", "--bs-success"]'
                      topProducts={data?.topProducts}
                    />
                  </>
                  )}
                </Card>
              </Col>

              <Col xl={4}>
                <Inquiries 
                customerInquiries={{
                  total: data?.inquiriesSummary.totalInquiries || 0,
                  pending: data?.inquiriesSummary.pendingInquiries || 0,
                  resolved: data?.inquiriesSummary.resolvedInquiries || 0
                }} 
              />
               <ProductCategories 
                  products={{
                    totalProducts: data?.productStats.totalProducts || 0,
                    publicProducts: data?.productStats.publicProducts || 0,
                    addonProducts: data?.productStats.addonProducts || 0,
                    venues: data?.venueCount || 0
                  }} 
                />

              </Col>
            </Row>
          </>
          }
        </Container>
      </div>
    </React.Fragment>
  );
}

export default Dashboard;